// src/pages/Onboarding.js
import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import './Onboarding.css';
import PgLogo from '../assets/pglogo.png';

/* ═══════════════════════════════════════════════════════════
   THE WALK

   The scene is drawn on a canvas with manual perspective
   projection. For a point at distance z:

     s       = FOCAL / z            (pixels per world unit)
     screenX = cx + worldX * s
     screenY = horizon + (CAM_H - worldY) * s

   so the ground (worldY = 0) sits at horizon + CAM_H * s.
   Everything — path, undergrowth, trees, waymarks and the
   trailhead sign — uses those three lines, which is why
   nothing can end up flat or upside down the way the old CSS
   rotateX plane did.

   THE PATH BENDS. Every prop stores `off`, its distance to
   the SIDE of the path rather than an absolute x. The path's
   centre at absolute distance zAbs is curveAt(zAbs), and we
   subtract curveAt(travel) so the bend is always centred on
   the camera and unwinds as you approach it.

   PACE: a full page scroll covers TOTAL_TRAVEL world units.
   Lower it to walk slower; SCROLL_EASE controls how much the
   scene drifts on after you stop. The card gaps and approach
   spacer in the CSS are the other half of the pacing.
   ═══════════════════════════════════════════════════════════ */

const CAM_H = 165;          // eye height above the path
const PATH_HALF = 112;      // half the trodden width
const HORIZON = 0.47;       // horizon line, fraction of viewport height
const FOCAL_RATIO = 1;      // focal length as a multiple of viewport height
const NEAR_Z = 120;         // nearest distance drawn (below the screen edge)
const DRAW_Z = 2000;        // furthest distance drawn
const FOG_START = 280;
const FOG_END = 1500;
const WORLD_LEN = 2400;     // the forest repeats every this many units
const TOTAL_TRAVEL = 1700;  // units covered by one full page scroll
const SCROLL_EASE = 0.045;  // lower = heavier, more drift after you stop
const SWAY = 7;             // camera sway, in pixels

/* Trailhead sign, in world units — must match .ob-welcome */
const SIGN_W = 340;
const SIGN_H = 430;
const SIGN_FADE_IN = 1500;
const SIGN_FADE_FULL = 850;

/* How far down the trail the foreground is taken to reach.
   This used to be the furthest walker's z, and it still sets
   the minimum distance for the sign — keeping it as a constant
   means the sign stands exactly where it did before the family
   was removed. */
const WALKER_REACH = 478;

const TAU = Math.PI * 2;
const FOG = [176, 196, 178];
const STEPS = 16;

/* the path's centre line, and its two wobbling edges */
const curveAt = (zAbs) =>
  Math.sin(zAbs / 760) * 215 + Math.sin(zAbs / 331) * 62;

const halfLeft = (zAbs) =>
  PATH_HALF + Math.sin(zAbs / 103) * 13 + Math.sin(zAbs / 47) * 7;

const halfRight = (zAbs) =>
  PATH_HALF + Math.sin(zAbs / 89 + 2.1) * 14 + Math.sin(zAbs / 53 + 0.7) * 6;

/* fixed set of distances the path polygon is sampled at */
const Z_SAMPLES = (() => {
  const out = [];
  let z = NEAR_Z;
  while (z < DRAW_Z) {
    out.push(z);
    z = z * 1.085 + 20;
  }
  out.push(DRAW_Z);
  return out;
})();

/* One colour ramp per material: index 0 = full colour up close,
   index STEPS = fully dissolved into the haze. Built once. */
const ramp = (rgb) => {
  const out = [];
  for (let i = 0; i <= STEPS; i += 1) {
    const f = i / STEPS;
    const r = Math.round(rgb[0] + (FOG[0] - rgb[0]) * f);
    const g = Math.round(rgb[1] + (FOG[1] - rgb[1]) * f);
    const b = Math.round(rgb[2] + (FOG[2] - rgb[2]) * f);
    out.push(`rgb(${r},${g},${b})`);
  }
  return out;
};

const TREE_PALETTES = [
  {
    light: ramp([96, 158, 82]),
    mid: ramp([49, 114, 57]),
    dark: ramp([24, 74, 38]),
    trunk: ramp([62, 45, 30]),
  },
  {
    light: ramp([80, 140, 70]),
    mid: ramp([38, 98, 48]),
    dark: ramp([18, 62, 32]),
    trunk: ramp([50, 36, 24]),
  },
  {
    light: ramp([108, 168, 90]),
    mid: ramp([56, 122, 62]),
    dark: ramp([28, 80, 42]),
    trunk: ramp([72, 52, 33]),
  },
  {
    // conifer — colder, darker
    light: ramp([64, 122, 82]),
    mid: ramp([30, 84, 58]),
    dark: ramp([16, 56, 40]),
    trunk: ramp([48, 36, 26]),
  },
];

const UNDERGROWTH = ramp([34, 76, 38]);
const UNDERGROWTH_HI = ramp([62, 112, 56]);
const FERN = ramp([44, 96, 50]);
const FERN_HI = ramp([84, 138, 66]);
const ROCK = ramp([116, 118, 110]);
const ROCK_DARK = ramp([72, 74, 70]);
const ROCK_MOSS = ramp([70, 104, 56]);
const BARK = ramp([74, 55, 36]);
const BARK_DARK = ramp([46, 33, 22]);
const TIMBER = ramp([124, 88, 51]);
const TIMBER_DARK = ramp([74, 51, 30]);
const BLAZE = ramp([0, 106, 83]);
const BLAZE_MARK = ramp([222, 220, 0]);
const CREAM = ramp([238, 233, 217]);
const PEBBLE = ramp([146, 134, 116]);
const PEBBLE_DARK = ramp([96, 86, 72]);
const LITTER = ramp([150, 112, 62]);
const ROOT = ramp([92, 68, 44]);
const ROOT_HI = ramp([130, 100, 66]);

const fogStep = (z) => {
  let f = (z - FOG_START) / (FOG_END - FOG_START);
  if (f < 0) f = 0;
  else if (f > 1) f = 1;
  return Math.round(f * STEPS);
};

const fogFade = (z) => 1 - fogStep(z) / STEPS;

/* Deterministic pseudo-random so the forest never reshuffles */
const rand = (seed) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

/* Everything along the trail, generated once. Thinner on
   phones: fewer tree rows and sparser undergrowth. */
const buildWorld = (small) => {
  const list = [];
  const rows = small ? 2 : 3;
  const treeStep = small ? 128 : 92;
  let seed = 1;
  const r = () => {
    seed += 1;
    return rand(seed);
  };

  for (let z = 0; z < WORLD_LEN; z += treeStep) {
    for (let side = -1; side <= 1; side += 2) {
      for (let row = 0; row < rows; row += 1) {
        const a = r();
        const b = r();
        const c = r();
        const d = r();
        const conifer = d < 0.4;

        list.push({
          kind: 'tree',
          conifer,
          off: side * (PATH_HALF + 60 + row * 400 + a * 300),
          z: (z + b * treeStep + row * 37) % WORLD_LEN,
          p: conifer ? 3 : Math.floor(c * 3) % 3,
          tw: (conifer ? 34 : 46) + c * 30,
          th: (conifer ? 300 : 430) + c * 240 - row * 24,
          cw: (conifer ? 210 : 300) + a * 190,
          ch: (conifer ? 820 : 680) + b * 340,
          // the near row leans its crown over the path
          lean: -side * (row === 0 ? 60 + c * 70 : 18),
        });
      }
    }
  }

  // grass and weeds crowding the edges of the trodden line
  for (let z = 0; z < WORLD_LEN; z += small ? 72 : 44) {
    for (let side = -1; side <= 1; side += 2) {
      list.push({
        kind: 'grass',
        off: side * (PATH_HALF + 2 + r() * 54),
        z: z + r() * 26,
        h: 30 + r() * 34,
        w: 30 + r() * 26,
        tone: r(),
      });
    }
  }

  for (let z = 0; z < WORLD_LEN; z += small ? 190 : 128) {
    list.push({
      kind: 'fern',
      off: (r() < 0.5 ? -1 : 1) * (PATH_HALF + 24 + r() * 220),
      z: z + r() * 60,
      h: 90 + r() * 70,
      w: 100 + r() * 70,
    });
  }

  for (let z = 0; z < WORLD_LEN; z += 186) {
    list.push({
      kind: 'rock',
      off: (r() < 0.5 ? -1 : 1) * (PATH_HALF + 10 + r() * 190),
      z: z + r() * 80,
      w: 40 + r() * 70,
      h: 26 + r() * 40,
      moss: r() < 0.55,
    });
  }

  for (let z = 140; z < WORLD_LEN; z += 640) {
    list.push({
      kind: 'log',
      off: (r() < 0.5 ? -1 : 1) * (PATH_HALF + 70 + r() * 120),
      z: z + r() * 90,
      w: 260 + r() * 160,
      h: 44 + r() * 22,
    });
  }

  // waymark posts, alternating sides
  let m = 0;
  for (let z = 90; z < WORLD_LEN; z += 360) {
    list.push({
      kind: 'marker',
      off: (m % 2 === 0 ? -1 : 1) * (PATH_HALF + 26),
      z,
      arrow: m % 3 === 0,
    });
    m += 1;
  }

  // the occasional fingerpost
  for (let z = 520; z < WORLD_LEN; z += 1120) {
    list.push({ kind: 'fingerpost', off: PATH_HALF + 40, z });
  }

  // loose stones and packed grit on the path itself
  for (let z = 0; z < WORLD_LEN; z += small ? 26 : 17) {
    list.push({
      kind: 'pebble',
      off: (r() - 0.5) * 1.7 * PATH_HALF,
      z: z + r() * 12,
      r: 3 + r() * 9,
      dark: r() < 0.4,
    });
  }

  // fallen leaves drifting over the edges
  for (let z = 0; z < WORLD_LEN; z += 54) {
    list.push({
      kind: 'litter',
      off: (r() - 0.5) * 2.4 * PATH_HALF,
      z: z + r() * 30,
      w: 26 + r() * 42,
    });
  }

  // roots breaking through the surface
  for (let z = 60; z < WORLD_LEN; z += 250) {
    list.push({ kind: 'root', z: z + r() * 90, t: 9 + r() * 9 });
  }

  return list;
};

const LIGHT_POOLS = [
  { off: -70, z: 240, r: 120 },
  { off: 60, z: 610, r: 96 },
  { off: -20, z: 940, r: 140 },
  { off: 110, z: 1290, r: 104 },
  { off: -130, z: 1660, r: 118 },
  { off: 20, z: 2050, r: 132 },
];

const steps = [
  {
    id: 1,
    num: '01',
    step: 'Step 1',
    title: 'Onboarding Documentation',
    body: 'Your onboarding journey will begin with a visit to the People & Culture department, where you will complete the necessary employment documentation, including recruitment, payroll, emergency contact and other administrative forms. This meeting also provides an opportunity to ask any initial questions and to gain an overview of key employment information.',
  },
  {
    id: 2,
    num: '02',
    step: 'Step 2',
    title: 'People & Culture / HR Induction',
    body: 'Attending this meeting is crucial, as it is designed to help you become familiar with the Agency, its services, mission, values, culture and ways of working. During this session, you will receive an overview of the departments, interesting statistics, employee benefits, internal initiatives, staff support services and other useful information to help you settle in. A guided tour of the premises will also be provided, allowing you to become acquainted with the workplace and key facilities.',
  },
  {
    id: 3,
    num: '03',
    step: 'Step 3',
    title: 'IT Onboarding',
    body: 'You will be introduced to the IT team, who will provide you with the equipment, access and technical resources required for your role while also providing guidance on the tools and technologies used across the Agency.',
  },
  {
    id: 4,
    num: '04',
    step: 'Step 4',
    title: 'Department Introduction',
    body: 'Your team will be waiting to welcome you to the department. You will learn more about your role, responsibilities, departmental objectives and ongoing projects, helping you settle in and feel part of the team from day one.',
  },
  {
    id: 5,
    num: '05',
    step: 'Step 5',
    title: 'Health & Safety Orientation',
    body: 'As part of your onboarding process, you will complete this session covering our clean desk policy and workplace health and safety procedures, including emergency protocols, safe working practices and other important guidelines designed to ensure a safe and secure working environment for all employees.',
  },
];

/* ── The trailhead sign at the end of the walk.
      The logo IS the name — there's no "Project Green" text
      line any more, so the logo carries the alt text. ── */
const WelcomeSign = React.forwardRef((props, ref) => (
  <div className="ob-welcome-layer">
    <div className="ob-welcome" ref={ref}>
      <span className="ob-welcome-shadow" />
      <span className="ob-welcome-leg ob-welcome-leg--l" />
      <span className="ob-welcome-leg ob-welcome-leg--r" />
      <span className="ob-welcome-brace" />

      <div className="ob-welcome-board">
        <div className="ob-welcome-inner">
          <span className="ob-welcome-bolt ob-welcome-bolt--tl" />
          <span className="ob-welcome-bolt ob-welcome-bolt--tr" />
          <span className="ob-welcome-bolt ob-welcome-bolt--bl" />
          <span className="ob-welcome-bolt ob-welcome-bolt--br" />

          <span className="ob-welcome-kicker">Welcome to</span>
          <img className="ob-welcome-logo" src={PgLogo} alt="Project Green" />
          <span className="ob-welcome-rule" />
          <span className="ob-welcome-sub">Your journey starts here.</span>
        </div>
      </div>
    </div>
  </div>
));

WelcomeSign.displayName = 'WelcomeSign';

const TrailScene = ({ welcomeRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const world = buildWorld(window.innerWidth < 760);
    const visible = [];

    const cam = {
      w: 0,
      h: 0,
      cx: 0,
      horizon: 0,
      focal: 0,
      stopZ: 520,
      depthScale: 1,
      sky: null,
      ground: null,
      path: null,
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;

      cam.w = w;
      cam.h = h;
      cam.cx = w / 2;
      cam.horizon = Math.round(h * HORIZON);
      cam.focal = h * FOCAL_RATIO;

      // on a narrow screen the foreground sits a little further
      // ahead, so it doesn't fill the viewport
      cam.depthScale = w < 760 ? 1.28 : 1;

      // how far down the trail the foreground reaches — the sign
      // must always stand well beyond this, so it arrives with
      // clear path in front of it
      const reach = WALKER_REACH * cam.depthScale;

      // size the sign so the WHOLE board fits inside the
      // viewport on both axes (not just width), then push it
      // back far enough that the walk ends with space before it
      const wantedByWidth = (SIGN_W * cam.focal) / (0.5 * w);
      const wantedByHeight = (SIGN_H * cam.focal) / (0.72 * h);
      const wanted = Math.max(wantedByWidth, wantedByHeight);
      cam.stopZ = Math.max(reach + 320, Math.min(1500, wanted));

      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      cam.sky = ctx.createLinearGradient(0, 0, 0, cam.horizon);
      cam.sky.addColorStop(0, '#8fb9d2');
      cam.sky.addColorStop(0.62, '#b1cbb8');
      cam.sky.addColorStop(1, `rgb(${FOG[0]},${FOG[1]},${FOG[2]})`);

      cam.ground = ctx.createLinearGradient(0, cam.horizon, 0, h);
      cam.ground.addColorStop(0, `rgb(${FOG[0]},${FOG[1]},${FOG[2]})`);
      cam.ground.addColorStop(0.14, '#537249');
      cam.ground.addColorStop(0.46, '#2b4726');
      cam.ground.addColorStop(1, '#16290f');

      cam.path = ctx.createLinearGradient(0, cam.horizon, 0, h * 1.05);
      cam.path.addColorStop(0, `rgb(${FOG[0]},${FOG[1]},${FOG[2]})`);
      cam.path.addColorStop(0.1, '#9d8a6a');
      cam.path.addColorStop(0.36, '#8a7350');
      cam.path.addColorStop(1, '#6d5738');
    };

    /* ── path geometry ── */
    let baseCurve = 0;

    const bendAt = (zAbs) => curveAt(zAbs) - baseCurve;

    // trace the trodden strip; widen/narrow it with `spread`
    const tracePath = (cx, travel, spread) => {
      ctx.beginPath();
      for (let i = 0; i < Z_SAMPLES.length; i += 1) {
        const z = Z_SAMPLES[i];
        const zAbs = travel + z;
        const s = cam.focal / z;
        const y = cam.horizon + CAM_H * s;
        const x = cx + (bendAt(zAbs) - halfLeft(zAbs) * spread) * s;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      for (let i = Z_SAMPLES.length - 1; i >= 0; i -= 1) {
        const z = Z_SAMPLES[i];
        const zAbs = travel + z;
        const s = cam.focal / z;
        const y = cam.horizon + CAM_H * s;
        ctx.lineTo(cx + (bendAt(zAbs) + halfRight(zAbs) * spread) * s, y);
      }
      ctx.closePath();
    };

    /* ── props ── */
    const drawTree = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const cw = o.cw * s;
      const bend = bendAt(travel + z);
      const x = cx + (bend + o.off) * s;
      const canopyX = cx + (bend + o.off + o.lean) * s;

      if (canopyX + cw < -40 || canopyX - cw > cam.w + 40) return;

      const step = fogStep(z);
      const pal = TREE_PALETTES[o.p];
      const base = cam.horizon + CAM_H * s;
      const th = o.th * s;
      const tw = o.tw * s;
      const ch = o.ch * s;

      ctx.fillStyle = pal.trunk[step];
      ctx.beginPath();
      ctx.moveTo(x - tw * 0.6, base);
      ctx.lineTo(x - tw * 0.26, base - th);
      ctx.lineTo(x + tw * 0.26, base - th);
      ctx.lineTo(x + tw * 0.6, base);
      ctx.closePath();
      ctx.fill();

      if (o.conifer) {
        // three stacked skirts of needles
        const top = base - th - ch;
        for (let i = 0; i < 3; i += 1) {
          const t = i / 3;
          const yTop = top + ch * t * 0.72;
          const yBot = top + ch * (t * 0.72 + 0.42);
          const half = (cw * 0.5) * (0.42 + t * 0.58);
          ctx.fillStyle = i === 0 ? pal.mid[step] : pal.dark[step];
          ctx.beginPath();
          ctx.moveTo(canopyX, yTop);
          ctx.lineTo(canopyX + half, yBot);
          ctx.lineTo(canopyX - half, yBot);
          ctx.closePath();
          ctx.fill();
        }
        if (z < 800) {
          ctx.fillStyle = pal.light[step];
          ctx.beginPath();
          ctx.moveTo(canopyX - cw * 0.06, top + ch * 0.06);
          ctx.lineTo(canopyX - cw * 0.02, top + ch * 0.4);
          ctx.lineTo(canopyX - cw * 0.22, top + ch * 0.4);
          ctx.closePath();
          ctx.fill();
        }
        return;
      }

      const cy = base - th - ch * 0.42;

      ctx.fillStyle = pal.dark[step];
      ctx.beginPath();
      ctx.ellipse(canopyX, cy + ch * 0.12, cw * 0.5, ch * 0.5, 0, 0, TAU);
      ctx.fill();

      ctx.fillStyle = pal.mid[step];
      ctx.beginPath();
      ctx.ellipse(canopyX - cw * 0.06, cy - ch * 0.06, cw * 0.44, ch * 0.46, 0, 0, TAU);
      ctx.fill();

      if (z < 900) {
        ctx.fillStyle = pal.light[step];
        ctx.beginPath();
        ctx.ellipse(canopyX - cw * 0.16, cy - ch * 0.3, cw * 0.22, ch * 0.24, 0, 0, TAU);
        ctx.fill();
      }
    };

    // one path, several blades, a single fill
    const drawTuft = (x, base, w, h, fill, blades) => {
      ctx.fillStyle = fill;
      ctx.beginPath();
      for (let i = 0; i < blades; i += 1) {
        const t = blades === 1 ? 0.5 : i / (blades - 1);
        const lean = (t - 0.5) * w;
        const tall = h * (0.62 + Math.sin(t * 3.1) * 0.38);
        ctx.moveTo(x + lean * 0.3 - w * 0.06, base);
        ctx.lineTo(x + lean, base - tall);
        ctx.lineTo(x + lean * 0.3 + w * 0.06, base);
      }
      ctx.closePath();
      ctx.fill();
    };

    const drawGrass = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      if (x < -30 || x > cam.w + 30) return;
      const step = fogStep(z);
      drawTuft(
        x,
        cam.horizon + CAM_H * s,
        o.w * s,
        o.h * s,
        o.tone > 0.66 ? UNDERGROWTH_HI[step] : UNDERGROWTH[step],
        5
      );
    };

    const drawFern = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      if (x < -60 || x > cam.w + 60) return;
      const step = fogStep(z);
      const base = cam.horizon + CAM_H * s;
      drawTuft(x, base, o.w * s, o.h * s, FERN[step], 7);
      if (z < 700) drawTuft(x, base, o.w * 0.5 * s, o.h * 0.66 * s, FERN_HI[step], 3);
    };

    const drawRock = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      const step = fogStep(z);
      const base = cam.horizon + CAM_H * s;
      const w = o.w * s;
      const h = o.h * s;

      ctx.fillStyle = ROCK_DARK[step];
      ctx.beginPath();
      ctx.ellipse(x, base, w * 0.5, h * 0.28, 0, 0, TAU);
      ctx.fill();

      ctx.fillStyle = ROCK[step];
      ctx.beginPath();
      ctx.moveTo(x - w * 0.5, base);
      ctx.lineTo(x - w * 0.3, base - h * 0.82);
      ctx.lineTo(x + w * 0.1, base - h);
      ctx.lineTo(x + w * 0.46, base - h * 0.5);
      ctx.lineTo(x + w * 0.5, base);
      ctx.closePath();
      ctx.fill();

      if (o.moss && z < 800) {
        ctx.fillStyle = ROCK_MOSS[step];
        ctx.beginPath();
        ctx.ellipse(x + w * 0.02, base - h * 0.78, w * 0.24, h * 0.18, 0, 0, TAU);
        ctx.fill();
      }
    };

    const drawLog = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      const step = fogStep(z);
      const base = cam.horizon + CAM_H * s;
      const w = o.w * s;
      const h = o.h * s;

      ctx.fillStyle = BARK[step];
      ctx.beginPath();
      ctx.ellipse(x, base - h * 0.5, w * 0.5, h * 0.5, 0, 0, TAU);
      ctx.fill();

      ctx.fillStyle = BARK_DARK[step];
      ctx.beginPath();
      ctx.ellipse(x, base - h * 0.28, w * 0.48, h * 0.3, 0, 0, TAU);
      ctx.fill();

      // sawn end
      ctx.fillStyle = TIMBER[step];
      ctx.beginPath();
      ctx.ellipse(x + w * 0.48, base - h * 0.5, h * 0.2, h * 0.5, 0, 0, TAU);
      ctx.fill();
    };

    const drawMarker = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      const step = fogStep(z);
      const base = cam.horizon + CAM_H * s;
      const at = (u) => base - u * s;

      ctx.fillStyle = TIMBER[step];
      ctx.fillRect(x - 9 * s, at(150), 18 * s, 150 * s);
      ctx.fillStyle = TIMBER_DARK[step];
      ctx.fillRect(x + 3 * s, at(150), 6 * s, 150 * s);

      ctx.fillStyle = BLAZE[step];
      ctx.fillRect(x - 9 * s, at(146), 18 * s, 34 * s);

      if (o.arrow) {
        ctx.fillStyle = BLAZE_MARK[step];
        ctx.beginPath();
        ctx.moveTo(x, at(142));
        ctx.lineTo(x + 7 * s, at(126));
        ctx.lineTo(x - 7 * s, at(126));
        ctx.closePath();
        ctx.fill();
      }
    };

    const drawFingerpost = (o, z, cx, travel) => {
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      const step = fogStep(z);
      const base = cam.horizon + CAM_H * s;
      const at = (u) => base - u * s;

      ctx.fillStyle = TIMBER[step];
      ctx.fillRect(x - 8 * s, at(230), 16 * s, 230 * s);

      const w = 130 * s;
      const h = 40 * s;
      const top = at(228);

      ctx.fillStyle = TIMBER_DARK[step];
      ctx.beginPath();
      ctx.moveTo(x - 4 * s, top);
      ctx.lineTo(x - 4 * s - w * 0.78, top);
      ctx.lineTo(x - 4 * s - w, top + h * 0.5);
      ctx.lineTo(x - 4 * s - w * 0.78, top + h);
      ctx.lineTo(x - 4 * s, top + h);
      ctx.closePath();
      ctx.fill();

      if (z < 900) {
        ctx.fillStyle = CREAM[step];
        ctx.fillRect(x - 18 * s - w * 0.62, top + h * 0.32, w * 0.5, h * 0.14);
        ctx.fillRect(x - 18 * s - w * 0.62, top + h * 0.58, w * 0.32, h * 0.12);
      }
    };

    const drawPebble = (o, z, cx, travel) => {
      if (z > 760) return;
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      const step = fogStep(z);
      const base = cam.horizon + CAM_H * s;
      ctx.fillStyle = o.dark ? PEBBLE_DARK[step] : PEBBLE[step];
      ctx.beginPath();
      ctx.ellipse(x, base, o.r * s, o.r * 0.5 * s, 0, 0, TAU);
      ctx.fill();
    };

    const drawLitter = (o, z, cx, travel) => {
      if (z > 900) return;
      const s = cam.focal / z;
      const x = cx + (bendAt(travel + z) + o.off) * s;
      const base = cam.horizon + CAM_H * s;
      ctx.globalAlpha = 0.42 * fogFade(z);
      ctx.fillStyle = LITTER[fogStep(z)];
      ctx.beginPath();
      ctx.ellipse(x, base, o.w * 0.5 * s, o.w * 0.16 * s, 0, 0, TAU);
      ctx.fill();
      ctx.globalAlpha = 1;
    };

    const drawRoot = (o, z, cx, travel) => {
      if (z > 900) return;
      const zAbs = travel + z;
      const s = cam.focal / z;
      const base = cam.horizon + CAM_H * s;
      const bend = bendAt(zAbs);
      const xl = cx + (bend - halfLeft(zAbs) * 0.95) * s;
      const xr = cx + (bend + halfRight(zAbs) * 0.95) * s;
      const step = fogStep(z);
      const t = o.t * s;

      ctx.fillStyle = ROOT[step];
      ctx.beginPath();
      ctx.moveTo(xl, base);
      ctx.quadraticCurveTo((xl + xr) / 2, base - t * 1.6, xr, base);
      ctx.quadraticCurveTo((xl + xr) / 2, base - t * 0.2, xl, base);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = ROOT_HI[step];
      ctx.beginPath();
      ctx.moveTo(xl, base - t * 0.2);
      ctx.quadraticCurveTo((xl + xr) / 2, base - t * 1.5, xr, base - t * 0.2);
      ctx.quadraticCurveTo((xl + xr) / 2, base - t * 1.1, xl, base - t * 0.2);
      ctx.closePath();
      ctx.fill();
    };

    /* ── one frame ── */
    const drawFrame = (travel) => {
      const cx = cam.cx + Math.sin(travel / 210) * SWAY;
      baseCurve = curveAt(travel);

      ctx.clearRect(0, 0, cam.w, cam.h);

      ctx.fillStyle = cam.sky;
      ctx.fillRect(0, 0, cam.w, cam.horizon + 1);

      ctx.fillStyle = cam.ground;
      ctx.fillRect(0, cam.horizon, cam.w, cam.h - cam.horizon);

      // scuffed soil spilling past the trodden edges
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = '#4c4029';
      tracePath(cx, travel, 1.22);
      ctx.fill();
      ctx.globalAlpha = 1;

      // the trodden line itself
      ctx.fillStyle = cam.path;
      tracePath(cx, travel, 1);
      ctx.fill();

      // the harder-packed centre, worn paler by boots
      ctx.globalAlpha = 0.32;
      ctx.fillStyle = '#b6a07a';
      tracePath(cx, travel, 0.52);
      ctx.fill();
      ctx.globalAlpha = 1;

      // pools of sun that make it through the canopy
      ctx.fillStyle = '#fff6cf';
      for (let i = 0; i < LIGHT_POOLS.length; i += 1) {
        const p = LIGHT_POOLS[i];
        let z = p.z - (travel % WORLD_LEN);
        if (z < NEAR_Z) z += WORLD_LEN;
        if (z > DRAW_Z) continue;
        const s = cam.focal / z;
        ctx.globalAlpha = 0.11 * fogFade(z);
        ctx.beginPath();
        ctx.ellipse(
          cx + (bendAt(travel + z) + p.off) * s,
          cam.horizon + CAM_H * s,
          p.r * s,
          p.r * s * 0.32,
          0,
          0,
          TAU
        );
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // everything along the trail, far to near
      const shift = ((travel % WORLD_LEN) + WORLD_LEN) % WORLD_LEN;
      visible.length = 0;
      for (let i = 0; i < world.length; i += 1) {
        const o = world[i];
        let z = o.z - shift;
        if (z < NEAR_Z) z += WORLD_LEN;
        if (z > DRAW_Z) continue;
        o.wz = z;
        visible.push(o);
      }
      visible.sort((a, b) => b.wz - a.wz);

      for (let i = 0; i < visible.length; i += 1) {
        const o = visible[i];
        const z = o.wz;
        if (o.kind === 'tree') drawTree(o, z, cx, travel);
        else if (o.kind === 'grass') drawGrass(o, z, cx, travel);
        else if (o.kind === 'fern') drawFern(o, z, cx, travel);
        else if (o.kind === 'rock') drawRock(o, z, cx, travel);
        else if (o.kind === 'log') drawLog(o, z, cx, travel);
        else if (o.kind === 'marker') drawMarker(o, z, cx, travel);
        else if (o.kind === 'fingerpost') drawFingerpost(o, z, cx, travel);
        else if (o.kind === 'pebble') drawPebble(o, z, cx, travel);
        else if (o.kind === 'litter') drawLitter(o, z, cx, travel);
        else drawRoot(o, z, cx, travel);
      }

      // the trailhead sign is a DOM element so the logo and the
      // text stay crisp — same projection maths, applied as a
      // transform instead of a fill. It stands on the path, so
      // it follows the bend too.
      const el = welcomeRef.current;
      if (el) {
        const zSign = TOTAL_TRAVEL + cam.stopZ - travel;
        if (zSign > SIGN_FADE_IN || zSign < 60) {
          el.style.opacity = '0';
        } else {
          const s = cam.focal / zSign;
          const y = cam.horizon + CAM_H * s;
          const x = cx + bendAt(travel + zSign) * s;
          let o = (SIGN_FADE_IN - zSign) / (SIGN_FADE_IN - SIGN_FADE_FULL);
          if (o > 1) o = 1;
          el.style.transform =
            `translate3d(${x - SIGN_W / 2}px, ${y - SIGN_H}px, 0) scale(${s})`;
          el.style.opacity = String(o);
        }
      }
    };

    /* ── scroll → travel, eased so the walk feels unhurried ── */
    const travelTarget = () => {
      const range = Math.max(
        1,
        document.documentElement.scrollHeight - window.innerHeight
      );
      const progress = Math.min(1, Math.max(0, (window.scrollY || 0) / range));
      return progress * TOTAL_TRAVEL;
    };

    let frame = null;
    let running = false;
    let current = 0;
    let target = 0;

    const tick = () => {
      const diff = target - current;

      if (Math.abs(diff) < 0.02) current = target;
      else current += diff * SCROLL_EASE;

      drawFrame(current);

      if (current !== target) {
        frame = window.requestAnimationFrame(tick);
      } else {
        frame = null;
        running = false;
      }
    };

    const onScroll = () => {
      target = travelTarget();

      if (!running) {
        running = true;
        frame = window.requestAnimationFrame(tick);
      }
    };

    const onResize = () => {
      resize();
      target = travelTarget();
      if (reduced) {
        current = target;
        drawFrame(current);
      } else if (!running) {
        running = true;
        frame = window.requestAnimationFrame(tick);
      }
    };

    resize();
    current = travelTarget();
    target = current;

    drawFrame(current);
    // the logo may still be decoding on first paint — redraw once it lands
    window.setTimeout(() => drawFrame(current), 120);

    window.addEventListener('resize', onResize);
    if (!reduced) window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [welcomeRef]);

  return (
    <div className="ob-scene" aria-hidden="true">
      <canvas className="ob-canvas" ref={canvasRef} />
      <div className="ob-canopy" />
      <div className="ob-sunbeams" />
      <div className="ob-vignette" />
    </div>
  );
};

const Onboarding = () => {
  const welcomeRef = useRef(null);

  return (
    <div className="onboarding-page">
      <NavBar />

      <TrailScene welcomeRef={welcomeRef} />
      <WelcomeSign ref={welcomeRef} />

      <div className="onboarding-page-content">
        <div className="onboarding-feed">
          {steps.map((item, index) => (
            <motion.div
              className="onboarding-card"
              key={item.id}
              initial={{ opacity: 0, filter: 'blur(6px)', y: 32 }}
              whileInView={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, ease: 'easeOut', delay: index * 0.05 }}
            >
              <div className="onboarding-card-left">
                <span className="onboarding-card-num">{item.num}</span>
              </div>
              <div className="onboarding-card-divider" />
              <div className="onboarding-card-right">
                <span className="onboarding-card-step">{item.step}</span>
                <h2 className="onboarding-card-title">{item.title}</h2>
                <p className="onboarding-card-body">{item.body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* the last stretch of trail, with nothing in the way */}
        <div className="onboarding-approach" />
      </div>

      <Footer />
    </div>
  );
};

export default Onboarding;